'use client'

import { ArrowBtn } from '@/app/components/atoms/ArrowBtn'
import type { RelatedVenueEntry } from '@/app/lib/api/types-api'
import { useCarouselProgress } from '@/app/lib/hooks/useCarouselProgress'
import { useDragScroll } from '@/app/lib/hooks/useDragScroll'
import { useRailScroll } from '@/app/lib/hooks/useRailScroll'
import { routes } from '@/app/lib/routing'
import { cx } from '@/app/lib/utils'

import { RecintoRelatedVenueCard } from './RecintoRelatedVenueCard'
import styles from './RecintoRelatedVenues.module.scss'

interface RecintoRelatedVenuesCarouselProps {
  readonly venues: readonly RelatedVenueEntry[]
}

/**
 * `RecintoRelatedVenuesCarousel` — carril horizontal en cualquier tamaño de
 * pantalla, con flechas de avanzar/retroceder superpuestas SOLO desde
 * escritorio (`useRailScroll`) y una barra de progreso continua en cualquier
 * tamaño (`useCarouselProgress`) — mismo patrón ya compartido por
 * `ProductCarousel` (módulo CMS «Productos») y `RankingCarousel`
 * («ProductosLocalizados»); paridad visual con `shared/CoverCarousel` de
 * `eci-firefly-uikit` (reimplementado, no copiado). En móvil/tablet el
 * desplazamiento es solo por gesto táctil, sin flechas.
 *
 * `'use client'` por las flechas y por el cálculo de progreso: el resto de
 * `RecintoRelatedVenues` es Server Component.
 */
export function RecintoRelatedVenuesCarousel({ venues }: RecintoRelatedVenuesCarouselProps) {
  const { railRef, atStart, atEnd, recalc: recalcArrows, scrollByStep } = useRailScroll(venues.length)
  // Con ratón el carril no se arrastra de forma nativa (solo el gesto táctil).
  useDragScroll(railRef, venues.length)
  const { hasOverflow, thumbWidth, thumbLeft, recalc: recalcProgress } = useCarouselProgress(railRef, venues.length)

  const handleScroll = (): void => {
    recalcArrows()
    recalcProgress()
  }

  return (
    <div className={styles.container}>
      <div className={styles.viewport}>
        <div ref={railRef} className={styles.rail} onScroll={handleScroll} data-testid="related-venues-carousel">
          {venues.map((venue) => (
            <div key={venue.id} className={styles.item}>
              <RecintoRelatedVenueCard
                title={venue.name}
                href={routes.recinto(venue.province_nicename, venue.url)}
                img={venue.image}
                location={venue.city || venue.province}
              />
            </div>
          ))}
        </div>

        <div className={styles.nav}>
          {!atStart && (
            <ArrowBtn
              dir="left"
              overlay
              className={cx(styles.navBtn, styles.navBtnLeft)}
              onClick={() => scrollByStep(-1)}
            />
          )}
          {!atEnd && (
            <ArrowBtn
              dir="right"
              overlay
              className={cx(styles.navBtn, styles.navBtnRight)}
              onClick={() => scrollByStep(1)}
            />
          )}
        </div>
      </div>

      {hasOverflow && (
        <div
          className={styles.progress}
          role="progressbar"
          aria-label="Progreso del carrusel de recintos relacionados"
          aria-valuenow={Math.round(thumbLeft)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className={styles.track}>
            <div className={styles.thumb} style={{ width: `${thumbWidth}%`, left: `${thumbLeft}%` }} />
          </div>
        </div>
      )}
    </div>
  )
}
